import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '@/api/http';
import { billingApi } from '@/api/endpoints';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BRAZILIAN_BANKS, bankDisplay } from '@/lib/banks-brazil';

export const Route = createFileRoute('/dashboard/financeiro')({ component: FinanceiroPage });

const STATUS_MAP: Record<string, { label: string; style: string }> = {
  OPEN:        { label: 'Aberto',         style: 'bg-slate-100 text-slate-700 border-slate-200' },
  CLOSED:      { label: 'Fechado',        style: 'bg-slate-100 text-slate-700 border-slate-200' },
  NF_PENDING:  { label: 'Aguardando NF',  style: 'bg-amber-50 text-amber-700 border-amber-200' },
  NF_REVIEW:   { label: 'NF em análise',  style: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  NF_APPROVED: { label: 'NF aprovada',    style: 'bg-teal-50 text-teal-700 border-teal-200' },
  NF_REJECTED: { label: 'NF rejeitada',   style: 'bg-red-50 text-red-600 border-red-200' },
  PAID:        { label: 'Pago',           style: 'bg-green-50 text-green-700 border-green-200' },
  DISPUTED:    { label: 'Em disputa',     style: 'bg-orange-50 text-orange-700 border-orange-200' },
};

const money = (v: unknown) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function FinanceiroPage() {
  const qc = useQueryClient();
  const { data: cycles = [], isLoading } = useQuery({ queryKey: ['cycles'], queryFn: billingApi.cycles });
  const { data: analytics } = useQuery({
    queryKey: ['my-analytics'],
    queryFn: () => api.get('therapists/me/analytics').json<any>(),
  });
  const { data: bankAccount } = useQuery({
    queryKey: ['my-bank-account'],
    queryFn: () => api.get('therapists/me/bank-account').json<any>(),
  });

  const [showBankForm, setShowBankForm] = useState(false);
  const [expandedCycle, setExpandedCycle] = useState<string | null>(null);
  const [nfFormCycle, setNfFormCycle] = useState<string | null>(null);
  const [nfNumber, setNfNumber] = useState('');
  const [nfFile, setNfFile] = useState<File | null>(null);
  const [nfError, setNfError] = useState<string | null>(null);

  const uploadInvoice = useMutation({
    mutationFn: async ({ cycleId, file, nfseNumber }: { cycleId: string; file: File; nfseNumber?: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      if (nfseNumber) fd.append('nfseNumber', nfseNumber);
      return api.post(`billing/cycles/${cycleId}/invoice/upload`, { body: fd, timeout: 60_000 }).json<any>();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cycles'] });
      setNfFormCycle(null); setNfNumber(''); setNfFile(null); setNfError(null);
    },
    onError: async (err: any) => {
      let msg = 'Falha no envio.';
      try {
        const body = await err?.response?.json?.();
        if (Array.isArray(body?.message)) msg = body.message.join(' · ');
        else if (typeof body?.message === 'string') msg = body.message;
      } catch { /* keep default */ }
      setNfError(msg);
    },
  });

  const hasBank = !!bankAccount;
  const feePercent = analytics?.platformFeePercent ?? 10;

  // Alerta de NF pendente — ciclos NF_PENDING / NF_REJECTED bloqueiam o repasse
  // do dia 1. Calcula urgência por proximidade (ou ultrapassagem) do prazo.
  const today = new Date();
  const todayMs = today.getTime();
  const pendingNFCycles = (cycles as any[])
    .filter((c) => c.id !== '__current__' && (c.status === 'NF_PENDING' || c.status === 'NF_REJECTED'))
    .map((c) => {
      const deadlineMs = new Date(c.nfDeadline).getTime();
      const daysLeft = Math.ceil((deadlineMs - todayMs) / 86_400_000);
      return { ...c, daysLeft, overdue: daysLeft < 0 };
    })
    .sort((a, b) => a.daysLeft - b.daysLeft);
  const mostUrgent = pendingNFCycles[0];

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Financeiro</h1>
        <p className="text-slate-500 text-sm mt-1">Receita líquida, ciclos de faturamento e Notas Fiscais</p>
      </div>

      {/* Alerta NF pendente — bloqueia repasse */}
      {mostUrgent && (
        <div
          className={cn(
            'rounded-xl border px-5 py-4 flex items-start justify-between gap-4 flex-wrap',
            mostUrgent.overdue
              ? 'border-red-300 bg-red-50'
              : mostUrgent.daysLeft <= 3
                ? 'border-amber-300 bg-amber-50'
                : 'border-coral-200 bg-coral-50/60',
          )}
        >
          <div className="flex items-start gap-3 min-w-0">
            <span className="text-2xl shrink-0">{mostUrgent.overdue ? '🚨' : mostUrgent.daysLeft <= 3 ? '⚠️' : '📄'}</span>
            <div className="min-w-0">
              <p
                className={cn(
                  'font-semibold text-sm',
                  mostUrgent.overdue ? 'text-red-800' : mostUrgent.daysLeft <= 3 ? 'text-amber-800' : 'text-coral-800',
                )}
              >
                {mostUrgent.overdue
                  ? `Nota fiscal atrasada há ${Math.abs(mostUrgent.daysLeft)} ${Math.abs(mostUrgent.daysLeft) === 1 ? 'dia' : 'dias'} — repasse retido`
                  : mostUrgent.status === 'NF_REJECTED'
                    ? 'Nota fiscal rejeitada — reenvie corrigida'
                    : mostUrgent.daysLeft === 0
                      ? 'Hoje é o último dia para enviar a Nota Fiscal'
                      : `Restam ${mostUrgent.daysLeft} ${mostUrgent.daysLeft === 1 ? 'dia' : 'dias'} para enviar a Nota Fiscal`}
              </p>
              <p
                className={cn(
                  'text-xs mt-0.5',
                  mostUrgent.overdue ? 'text-red-700' : mostUrgent.daysLeft <= 3 ? 'text-amber-700' : 'text-coral-700',
                )}
              >
                Ciclo {new Date(mostUrgent.periodStart).toLocaleDateString('pt-BR')} → {new Date(mostUrgent.periodEnd).toLocaleDateString('pt-BR')}
                {' · '}
                <strong>{money(mostUrgent.netAmount)}</strong> a receber{mostUrgent.overdue ? ' (bloqueado até envio)' : ''}
                {' · '}prazo {new Date(mostUrgent.nfDeadline).toLocaleDateString('pt-BR')}
                {pendingNFCycles.length > 1 && ` · +${pendingNFCycles.length - 1} ${pendingNFCycles.length - 1 === 1 ? 'ciclo aguardando NF' : 'ciclos aguardando NF'}`}
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              setNfFormCycle(mostUrgent.id);
              setExpandedCycle(mostUrgent.id);
              setTimeout(() => {
                document.getElementById(`cycle-${mostUrgent.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
            className={cn(
              'shrink-0 text-white',
              mostUrgent.overdue
                ? 'bg-red-600 hover:bg-red-700'
                : mostUrgent.daysLeft <= 3
                  ? 'bg-amber-500 hover:bg-amber-600'
                  : 'bg-coral-500 hover:bg-coral-600',
            )}
          >
            Enviar NF agora
          </Button>
        </div>
      )}

      {/* Aviso bancário */}
      {!hasBank && bankAccount !== undefined && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-amber-800 text-sm">Dados bancários não cadastrados</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Cadastre sua conta bancária e chave PIX para receber os repasses.
            </p>
          </div>
          <Button
            onClick={() => {
              setShowBankForm(true);
              setTimeout(() => {
                document.getElementById('bank-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            Cadastrar
          </Button>
        </div>
      )}

      {/* KPIs */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi
            label="Mês atual (líquido)"
            value={money(analytics.monthNetRevenue)}
            sub={
              analytics.revenueDelta != null
                ? `${analytics.revenueDelta >= 0 ? '↑' : '↓'} ${Math.abs(analytics.revenueDelta).toFixed(0)}% vs anterior`
                : `${analytics.monthSessions} sessões`
            }
            tone={analytics.revenueDelta != null && analytics.revenueDelta < 0 ? 'coral' : 'teal'}
          />
          <Kpi
            label="Mês anterior"
            value={money(analytics.lastMonthNetRevenue)}
            sub={`${analytics.lastMonthSessions} sessões · taxa ${feePercent}%`}
          />
          <Kpi
            label="Total recebido"
            value={money(analytics.totalLifetime)}
            sub={`${analytics.totalSessions} sessões totais`}
            tone="teal"
          />
          <Kpi
            label="Cancelamento"
            value={`${analytics.cancellationRate.toFixed(1)}%`}
            sub={`${analytics.statusMap?.CANCELLED ?? 0} canceladas`}
            tone={analytics.cancellationRate > 15 ? 'coral' : undefined}
          />
        </div>
      )}

      {/* Receita 6 meses */}
      {analytics?.byMonth?.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-slate-800 mb-4">Receita dos últimos 6 meses (bruta)</h3>
            <BarChart data={analytics.byMonth.map((m: any) => ({ label: m.month, value: m.revenue }))} format={(v) => v > 0 ? `R$${v.toFixed(0)}` : '—'} />
          </CardContent>
        </Card>
      )}

      {/* Bank account */}
      <div id="bank-form-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-slate-800">Dados bancários para repasse</h2>
          {hasBank && !showBankForm && (
            <Button variant="outline" onClick={() => setShowBankForm(true)} className="text-sm">Editar</Button>
          )}
        </div>

        {hasBank && !showBankForm ? (
          <Card>
            <CardContent className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Stat label="Banco" value={bankDisplay(bankAccount.bankCode)} />
              <Stat label="Agência" value={bankAccount.agency} />
              <Stat label="Conta" value={`${bankAccount.accountNumber} (${bankAccount.accountType})`} />
              {bankAccount.pixKey && (
                <div className="col-span-2 md:col-span-3">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wide">
                    Chave PIX ({bankAccount.pixKeyType ?? '—'})
                  </div>
                  <div className="font-mono font-semibold">{bankAccount.pixKey}</div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          showBankForm || !hasBank ? <BankForm initial={bankAccount} onDone={() => setShowBankForm(false)} /> : null
        )}
      </div>

      {/* Ciclos */}
      <div>
        <h2 className="font-bold text-slate-800 mb-3">Ciclos de faturamento</h2>
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-12 text-center text-sm text-slate-400">Carregando...</div>
            ) : cycles.length === 0 ? (
              <div className="p-12 text-center text-sm text-slate-400">
                Nenhum ciclo ainda. Eles são criados automaticamente quando você concluir sessões.
              </div>
            ) : (
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-xs">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Período</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-right px-4 py-3 font-semibold">Sessões</th>
                    <th className="text-right px-4 py-3 font-semibold">Bruto</th>
                    <th className="text-right px-4 py-3 font-semibold">Taxa</th>
                    <th className="text-right px-4 py-3 font-semibold">A Receber</th>
                    <th className="text-right px-4 py-3 font-semibold">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(cycles as any[]).map((c) => {
                    const meta = c.id === '__current__'
                      ? { label: 'Aberto', style: 'bg-teal-50 text-teal-700 border-teal-200' }
                      : STATUS_MAP[c.status] ?? { label: c.status, style: 'bg-slate-100 text-slate-600' };
                    const canSubmit = c.status === 'NF_PENDING' || c.status === 'NF_REJECTED';
                    const isExpanded = expandedCycle === c.id;
                    const isFormOpen = nfFormCycle === c.id;
                    const hasSessions = (c.sessions ?? []).length > 0;
                    return (
                      <>
                        <tr key={c.id} id={`cycle-${c.id}`} className={cn('hover:bg-slate-50/60', c.status === 'PAID' && 'opacity-60')}>
                          <td className="px-4 py-3 text-xs text-slate-700">
                            <div className="font-medium">
                              {new Date(c.periodStart).toLocaleDateString('pt-BR')} → {new Date(c.periodEnd).toLocaleDateString('pt-BR')}
                            </div>
                            <div className="text-slate-400 mt-0.5 text-[10px]">
                              NF: {new Date(c.nfDeadline).toLocaleDateString('pt-BR')} · Repasse: {new Date(c.payoutDate).toLocaleDateString('pt-BR')}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', meta.style)}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600 text-xs">{c.totalSessions}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700 text-xs">{money(c.grossAmount)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-500 text-xs">- {money(c.platformFee)}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-bold text-teal-700">{money(c.netAmount)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              {canSubmit && (
                                <Button
                                  size="sm"
                                  onClick={() => setNfFormCycle(isFormOpen ? null : c.id)}
                                  className={cn('h-7 text-xs px-2.5', isFormOpen ? 'bg-slate-200 text-slate-600' : 'bg-coral-400 hover:bg-coral-500 text-white')}
                                >
                                  {isFormOpen ? 'Cancelar' : 'Enviar NF'}
                                </Button>
                              )}
                              {c.invoiceNF?.nfsePdfUrl && !canSubmit && (
                                <a href={c.invoiceNF.nfsePdfUrl} target="_blank" rel="noreferrer" className="text-[11px] text-teal-600 hover:underline">
                                  Ver NF →
                                </a>
                              )}
                              {hasSessions && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedCycle(isExpanded ? null : c.id)}
                                  className="text-[11px] text-teal-600 hover:underline whitespace-nowrap"
                                >
                                  {isExpanded ? '▲ sessões' : '▼ sessões'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && hasSessions && (
                          <tr key={`${c.id}-sessions`}>
                            <td colSpan={7} className="bg-slate-50/40 px-4 py-4">
                              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                Sessões ({c.sessions.length})
                              </p>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-400 uppercase tracking-wide text-[10px]">
                                    <th className="text-left px-3 py-2 font-semibold">Data</th>
                                    <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                                    <th className="text-left px-3 py-2 font-semibold">Modalidade</th>
                                    <th className="text-center px-3 py-2 font-semibold">Plano</th>
                                    <th className="text-right px-3 py-2 font-semibold">Cobrado</th>
                                    <th className="text-right px-3 py-2 font-semibold">Taxa</th>
                                    <th className="text-right px-3 py-2 font-semibold">Líquido</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {c.sessions.map((s: any) => {
                                    const price = Number(s.priceCharged ?? 0);
                                    const pct = Number(s.platformFeePercent ?? 0);
                                    const fee = price * (pct / 100);
                                    const net = price - fee;
                                    const planLabel = s.planSnapshot ?? '—';
                                    const planClass =
                                      planLabel === 'PREMIUM' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                      planLabel === 'PRO' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                                      'bg-slate-50 text-slate-600 border-slate-200';
                                    return (
                                      <tr key={s.id} className="border-t border-slate-100">
                                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                          {new Date(s.scheduledAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700 truncate max-w-[200px]">
                                          {s.client?.user?.name ?? '—'}
                                        </td>
                                        <td className="px-3 py-2 text-slate-500">
                                          {s.modality === 'ONLINE' ? 'Online' : s.modality === 'PRESENCIAL' ? 'Presencial' : 'Híbrido'}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', planClass)}>
                                            {planLabel}{pct > 0 && ` · ${pct}%`}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{money(price)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-400">- {money(fee)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums font-bold text-teal-700">{money(net)}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                        {isFormOpen && canSubmit && (
                          <tr key={`${c.id}-form`}>
                            <td colSpan={7} className="bg-slate-50/60 px-4 py-4">
                              <div className="space-y-3 max-w-2xl">
                                {c.invoiceNF?.rejectionReason && (
                                  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
                                    <strong>NF rejeitada:</strong> {c.invoiceNF.rejectionReason}
                                  </div>
                                )}
                                <div className="grid md:grid-cols-2 gap-3">
                                  <Field label="Número da NFSe (opcional)">
                                    <input value={nfNumber} onChange={(e) => setNfNumber(e.target.value)} className="input" placeholder="2026000042" />
                                  </Field>
                                  <Field label="Arquivo PDF da NF">
                                    <input
                                      type="file"
                                      accept="application/pdf"
                                      onChange={(e) => { setNfFile(e.target.files?.[0] ?? null); setNfError(null); }}
                                      className="input pt-1.5"
                                    />
                                  </Field>
                                </div>
                                {nfFile && (
                                  <p className="text-xs text-slate-500">
                                    📄 <strong>{nfFile.name}</strong> · {(nfFile.size / 1024).toFixed(1)} KB
                                  </p>
                                )}
                                {nfError && (
                                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                    {nfError}
                                  </p>
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => {
                                      if (!nfFile) return;
                                      setNfError(null);
                                      uploadInvoice.mutate({ cycleId: c.id, file: nfFile, nfseNumber: nfNumber || undefined });
                                    }}
                                    disabled={!nfFile || uploadInvoice.isPending}
                                    className="bg-teal-500 hover:bg-teal-600 text-white"
                                  >
                                    {uploadInvoice.isPending ? 'Enviando...' : 'Confirmar envio'}
                                  </Button>
                                  <Button variant="outline" onClick={() => { setNfFormCycle(null); setNfFile(null); setNfError(null); }}>
                                    Fechar
                                  </Button>
                                </div>
                                <p className="text-[11px] text-slate-400">
                                  Aceita arquivos PDF de até 10 MB. O PDF fica disponível pra revisão da equipe Elo.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:38px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </div>
  );
}

function BankForm({ initial, onDone }: { initial: any; onDone: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset } = useForm<any>();

  useEffect(() => {
    reset({
      bankCode: initial?.bankCode ?? '',
      agency: initial?.agency ?? '',
      accountNumber: initial?.accountNumber ?? '',
      accountType: initial?.accountType ?? 'corrente',
      pixKey: initial?.pixKey ?? '',
      pixKeyType: initial?.pixKeyType ?? '',
    });
  }, [initial, reset]);

  const save = useMutation({
    mutationFn: (data: any) => api.put('therapists/me/bank-account', { json: data }).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-bank-account'] });
      onDone();
    },
  });

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={handleSubmit((data) => save.mutate(data))} className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Banco *">
              <select {...register('bankCode', { required: true })} className="input">
                <option value="">Selecione…</option>
                {BRAZILIAN_BANKS.map((b) => (
                  <option key={`${b.code}-${b.shortName}`} value={b.code}>
                    {b.shortName} ({b.code})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Agência *">
              <input {...register('agency', { required: true })} placeholder="0001" className="input" />
            </Field>
            <Field label="Número da conta *">
              <input {...register('accountNumber', { required: true })} placeholder="12345-6" className="input" />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Tipo de conta">
              <select {...register('accountType')} className="input">
                <option value="corrente">Corrente</option>
                <option value="poupanca">Poupança</option>
              </select>
            </Field>
            <Field label="Tipo de chave PIX">
              <select {...register('pixKeyType')} className="input">
                <option value="">Selecione…</option>
                <option value="CPF">CPF</option>
                <option value="CNPJ">CNPJ</option>
                <option value="EMAIL">E-mail</option>
                <option value="PHONE">Telefone</option>
                <option value="EVP">Chave aleatória (EVP)</option>
              </select>
            </Field>
          </div>
          <Field label="Chave PIX">
            <input {...register('pixKey')} placeholder="Sua chave PIX" className="input" />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" disabled={save.isPending} className="bg-teal-500 hover:bg-teal-600 text-white">
              {save.isPending ? 'Salvando...' : 'Salvar dados bancários'}
            </Button>
            {initial && <Button type="button" variant="outline" onClick={onDone}>Cancelar</Button>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'teal' | 'coral' }) {
  const styles =
    tone === 'teal' ? 'border-teal-100 bg-teal-50/40 text-teal-900' :
    tone === 'coral' ? 'border-coral-100 bg-coral-50/40 text-coral-900' :
    'border-slate-200 bg-white text-slate-900';
  return (
    <div className={cn('rounded-xl border p-4', styles)}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-xl font-extrabold mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function BarChart({ data, format }: { data: { label: string; value: number }[]; format: (v: number) => string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="grid h-56" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))`, gap: '0.75rem' }}>
      {data.map((d) => {
        const h = (d.value / max) * 100;
        return (
          <div key={d.label} className="flex flex-col h-full justify-end items-center">
            <div className="w-full max-w-[60px] flex flex-col items-center justify-end h-full relative">
              {d.value > 0 && (
                <span className="text-[10px] text-slate-500 font-semibold mb-1 tabular-nums">
                  {format(d.value)}
                </span>
              )}
              <div
                className={d.value > 0 ? 'w-full bg-teal-500 rounded-t-md' : 'w-full bg-slate-200 rounded-full'}
                style={{ height: d.value > 0 ? `${Math.max(h * 1.6, 4)}px` : '2px' }}
              />
            </div>
            <div className="text-[11px] text-slate-500 text-center mt-2 capitalize">
              {String(d.label).split(' ')[0]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
